/** XeAgents.ai - "Free Pilot" lead capture + demo booking **/
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Rocket, Loader2, CalendarCheck, CheckCircle2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { isWorkEmail, WORK_EMAIL_ERROR_MESSAGE } from "@shared/work-email";
import { COUNTRIES, countryFromTimezone, normalizeCountry } from "@shared/countries";
import { CalEmbed, CAL_BOOKING_URL, DEMO_EVENT, PILOT_CREDITS, type CalPrefill } from "@/components/landing/CalEmbed";
import { withBase } from "@/lib/base-path";

/**
 * Four fields. Every optional extra is friction on the one screen that decides
 * whether the lead exists at all, so phone, team size and the free-text goal
 * are gone - and because team size used to default to "1-10", it is no longer
 * sent either. A hidden default stores an answer nobody gave.
 */
const pilotRequestSchema = z.object({
  fullName: z.string().min(2, "Please enter your full name"),
  workEmail: z.string().email("Please enter a valid work email").refine(isWorkEmail, WORK_EMAIL_ERROR_MESSAGE),
  company: z.string().min(1, "Company name is required"),
  country: z.string().min(1, "Please select your country"),
});

type PilotRequestFormData = z.infer<typeof pilotRequestSchema>;

/**
 * details   - nothing has been sent yet; Continue only advances the screen
 * booking   - the calendar, and the Submit Request button beside it
 * done      - confirmation, worded by whether they actually booked
 */
type Step = "details" | "booking" | "done";

interface PilotRequestDialogContextType {
  open: () => void;
}

const PilotRequestDialogContext = createContext<PilotRequestDialogContextType | null>(null);

export function usePilotRequestDialog() {
  const context = useContext(PilotRequestDialogContext);
  if (!context) {
    // Pages outside the provider (Blog, Contact) navigate to the landing page,
    // which opens the dialog on mount - mirrors useAuthDialog.
    return {
      open: () => {
        window.location.href = withBase("/?pilotRequest=1");
      },
    };
  }
  return context;
}

export function PilotRequestDialogProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("pilotRequest") === "1") {
      setIsOpen(true);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  return (
    <PilotRequestDialogContext.Provider value={{ open: () => setIsOpen(true) }}>
      {children}
      <PilotRequestDialogContent isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </PilotRequestDialogContext.Provider>
  );
}

function PilotRequestDialogContent({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { toast } = useToast();
  const [step, setStep] = useState<Step>("details");
  const [prefill, setPrefill] = useState<CalPrefill>({});
  const [didBook, setDidBook] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // The booking callback and a still-visible Submit Request button can both
  // fire. This blocks the second, and resets on failure so a genuine retry
  // still works.
  const sentRef = useRef(false);

  const form = useForm<PilotRequestFormData>({
    resolver: zodResolver(pilotRequestSchema),
    defaultValues: { fullName: "", workEmail: "", company: "", country: "" },
  });

  // Reset on each open, so someone returning is not shown a stale confirmation.
  useEffect(() => {
    if (!isOpen) return;
    setStep("details");
    setPrefill({});
    setDidBook(false);
    setSubmitting(false);
    sentRef.current = false;
    form.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  /**
   * Pre-fill the country from the server's view of the request, falling back to
   * the browser timezone. A failed lookup leaves the field EMPTY on purpose: a
   * wrong pre-filled answer is worse than none, because nobody re-reads a
   * field that already has something in it.
   */
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      let detected: string | null = null;
      try {
        const res = await fetch("/api/geo/country");
        if (res.ok) detected = normalizeCountry((await res.json())?.country);
      } catch {
        // Ignore - the timezone fallback below is the whole point.
      }
      if (!detected) {
        try {
          detected = countryFromTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
        } catch {
          detected = null;
        }
      }
      if (!cancelled && detected && !form.getValues("country")) {
        form.setValue("country", detected, { shouldValidate: false });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  /**
   * Send the lead.
   *
   * `booked` is an ARGUMENT, not read from state: the booking callback runs
   * before React has applied setDidBook, so reading state here would report the
   * pre-update value and confirm the wrong thing.
   */
  const submitRequest = useCallback(
    async (booked: boolean) => {
      if (sentRef.current) return;
      sentRef.current = true;
      setSubmitting(true);
      try {
        const values = form.getValues();
        await apiRequest("POST", "/api/pilot-requests", { ...values, booked });
        setDidBook(booked);
        setStep("done");
      } catch (error: any) {
        // Let them try again rather than stranding a filled-in form.
        sentRef.current = false;
        toast({
          title: "Couldn't submit your request",
          description: error?.message || "Please try again.",
          variant: "destructive",
        });
      } finally {
        setSubmitting(false);
      }
    },
    [form, toast],
  );

  // A completed booking ends the flow: submit for them and show the
  // confirmation. Leaving the calendar up would sit the visitor in front of the
  // slot picker they just used, which reads as a booking that failed.
  const handleBookingSuccess = useCallback(() => {
    void submitRequest(true);
  }, [submitRequest]);

  const onContinue = (data: PilotRequestFormData) => {
    // Nothing is sent here. The lead is written on step 2.
    setPrefill({ name: data.fullName, email: data.workEmail });
    setStep("booking");
  };

  const isBooking = step === "booking";

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className={
          isBooking
            ? "sm:max-w-4xl h-[90vh] p-0 overflow-hidden flex flex-col"
            : "sm:max-w-lg max-h-[90vh] overflow-y-auto"
        }
      >
        {step === "details" && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Rocket className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                Request a Free Pilot
              </DialogTitle>
              <DialogDescription>
                Your pilot includes{" "}
                <span className="font-medium text-foreground">{PILOT_CREDITS} credits</span> to run real calls.
                Next you'll pick a time for a {DEMO_EVENT.durationLabel} setup call on {DEMO_EVENT.platform}.
              </DialogDescription>
            </DialogHeader>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onContinue)} className="space-y-4" data-testid="form-pilot-request">
                <FormField
                  control={form.control}
                  name="fullName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Full Name *</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter your full name" data-testid="input-pilot-name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="workEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Work Email *</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="you@company.com" data-testid="input-pilot-email" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="company"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company *</FormLabel>
                      <FormControl>
                        <Input placeholder="Your company name" data-testid="input-pilot-company" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="country"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Country *</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-pilot-country">
                            <SelectValue placeholder="Select your country" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {COUNTRIES.map((c) => (
                            <SelectItem key={c.value} value={c.value}>
                              {c.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="button-pilot-continue"
                >
                  Continue
                </Button>
              </form>
            </Form>
          </>
        )}

        {step === "booking" && (
          <>
            <DialogHeader className="px-6 pt-6 pb-3 shrink-0">
              <DialogTitle className="flex items-center gap-2">
                <CalendarCheck className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                Pick a time for your setup call
              </DialogTitle>
              <DialogDescription>
                A {DEMO_EVENT.durationLabel} call on {DEMO_EVENT.platform}. Booking submits your request
                automatically — or send it without a time and we'll follow up.
              </DialogDescription>
            </DialogHeader>

            <div className="flex-1 min-h-0 px-2">
              <CalEmbed prefill={prefill} onBookingSuccess={handleBookingSuccess} />
            </div>

            <div className="shrink-0 border-t px-6 py-3 flex items-center justify-between gap-4">
              <p className="text-xs text-muted-foreground">
                Includes{" "}
                <span className="font-medium text-foreground">{PILOT_CREDITS} credits</span> for real calls.
              </p>
              <Button
                onClick={() => void submitRequest(false)}
                disabled={submitting}
                className="shrink-0 bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="button-pilot-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit Request"
                )}
              </Button>
            </div>
          </>
        )}

        {step === "done" && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                {didBook ? "You're booked" : "Request received"}
              </DialogTitle>
              <DialogDescription>
                {didBook ? (
                  <>
                    Your request is in and your {DEMO_EVENT.durationLabel} call is on the calendar. The{" "}
                    {DEMO_EVENT.platform} invite is on its way to your inbox.
                  </>
                ) : (
                  <>
                    Your request is in — we'll be in touch. If you'd rather pick a time now,{" "}
                    <a
                      href={CAL_BOOKING_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline dark:text-blue-400"
                      data-testid="link-pilot-book-later"
                    >
                      open the booking page
                    </a>
                    .
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="pt-2">
              <Button
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                onClick={onClose}
                data-testid="button-pilot-done"
              >
                Done
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default PilotRequestDialogProvider;
